/** Campi display per movimento registro (form semplificato o MovimentoModel RENTRI). */

export type MovimentoRecord = Record<string, unknown>;

const CAUSALI_CS: Record<string, string> = {
  Carico: "Carico",
  Scarico: "Scarico",
  CaricoScarico: "Carico e scarico",
};

const CAUSALI: Record<string, string> = {
  DT: "Prodotto o detenuto nell'unità locale",
  NP: "Nuovo produttore",
  "T*": "Ricevuto da terzi",
  RE: "Prodotto al di fuori dell'unità locale",
  I: "Scarico interno",
  aT: "Scarico a terzi",
  M: "Scarico per produzione di materiali",
  TR: "Intermediario",
  "T*aT": "Carico e scarico",
};

const STATI_FISICI: Record<string, string> = {
  SP: "In polvere o pulverulento",
  S: "Solido",
  FP: "Fangoso",
  L: "Liquido",
  VS: "Vischioso sciropposo",
};

const PROVENIENZA: Record<string, string> = {
  U: "Urbano",
  S: "Speciale",
};

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

export function movimentoRiferimenti(m: MovimentoRecord): Record<string, unknown> {
  return asRecord(m.riferimenti);
}

export function movimentoRifiuto(m: MovimentoRecord): Record<string, unknown> {
  return asRecord(m.rifiuto);
}

export function formatEerDisplay(eer: unknown): string {
  const raw = String(eer ?? "").replace(/\./g, "").trim();
  if (!raw) return "—";
  if (raw.length === 6) {
    return `${raw.slice(0, 2)}.${raw.slice(2, 4)}.${raw.slice(4, 6)}`;
  }
  return raw;
}

function formatDateIt(raw: unknown, withTime = false): string {
  if (!raw) return "—";
  const d = new Date(String(raw));
  if (isNaN(d.getTime())) return "—";
  if (withTime) {
    return d.toLocaleString("it-IT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return d.toLocaleDateString("it-IT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export function movimentoIdentificativo(m: MovimentoRecord): string {
  const rif = movimentoRiferimenti(m);
  const num = asRecord(rif.numero_registrazione);
  return String(num.identificativo ?? m.identificativo_movimento ?? "—");
}

export function movimentoNumeroRegistrazione(m: MovimentoRecord): string {
  const rif = movimentoRiferimenti(m);
  const num = asRecord(rif.numero_registrazione);
  const anno = num.anno;
  const prog = num.progressivo;
  if (anno != null && prog != null) {
    return `${anno}/${String(prog).padStart(8, "0")}`;
  }
  return "—";
}

export function movimentoCausaleCs(m: MovimentoRecord): string {
  const rif = movimentoRiferimenti(m);
  const cs = String(rif.causale_operazione_cs ?? m.causale ?? "");
  return (CAUSALI_CS[cs] ?? cs) || "—";
}

export function movimentoCausaleCodice(m: MovimentoRecord): string {
  const rif = movimentoRiferimenti(m);
  return String(rif.causale_operazione ?? m.causale_codice ?? "");
}

export function movimentoCausaleLabel(m: MovimentoRecord): string {
  const code = movimentoCausaleCodice(m);
  if (!code) return "—";
  return CAUSALI[code] ?? code;
}

export function movimentoCausaleCompleta(m: MovimentoRecord): string {
  const code = movimentoCausaleCodice(m);
  const label = movimentoCausaleLabel(m);
  if (!code) return movimentoCausaleCs(m);
  return `${code} - ${label}`;
}

export function movimentoHeaderSubtitle(m: MovimentoRecord): string {
  const parts = [
    movimentoNumeroRegistrazione(m),
    movimentoCausaleCs(m),
    movimentoCausaleCompleta(m),
  ].filter((p) => p && p !== "—");
  return parts.join(" | ");
}

export function movimentoRowFields(m: MovimentoRecord) {
  const rif = movimentoRiferimenti(m);
  const rifiuto = movimentoRifiuto(m);
  const qty = asRecord(rifiuto.quantita ?? m.quantita);
  const dataRaw = rif.data_ora_registrazione ?? m.data ?? m.data_movimento;
  const data =
    typeof dataRaw === "string" && dataRaw.includes("T")
      ? dataRaw.slice(0, 10)
      : dataRaw;
  return {
    identificativo: movimentoIdentificativo(m),
    numero: movimentoNumeroRegistrazione(m),
    data: data != null ? formatDateIt(data) : "—",
    causale: movimentoCausaleCs(m),
    causaleDettaglio: movimentoCausaleCompleta(m),
    eer: formatEerDisplay(rifiuto.codice_eer ?? m.eer ?? m.codice_eer),
    quantita: String(qty.valore ?? m.quantita ?? "—"),
    um: String(qty.unita_misura ?? qty.unitaMisura ?? m.unita_misura ?? m.um ?? "—"),
    trasmesso: formatDateIt(rif.data_trasmissione, true),
  };
}

export type MovimentoTabId =
  | "riferimenti"
  | "rifiuto"
  | "integrazione"
  | "esito"
  | "provenienza"
  | "annotazioni";

export function movimentoTabHasData(m: MovimentoRecord, tab: MovimentoTabId): boolean {
  switch (tab) {
    case "riferimenti":
      return Object.keys(movimentoRiferimenti(m)).length > 0;
    case "rifiuto":
      return Object.keys(movimentoRifiuto(m)).length > 0;
    case "integrazione":
      return Object.keys(asRecord(m.integrazione_fir)).length > 0;
    case "esito":
      return Object.keys(asRecord(m.esito)).length > 0;
    case "provenienza":
      return (
        Object.keys(asRecord(m.produttore)).length > 0 ||
        Object.keys(asRecord(m.trasportatore)).length > 0 ||
        Object.keys(asRecord(m.destinatario)).length > 0 ||
        !!movimentoRifiuto(m).provenienza
      );
    case "annotazioni":
      return !!String(m.annotazioni ?? "").trim();
    default:
      return false;
  }
}

export function movimentoRiferimentiCampi(m: MovimentoRecord): { label: string; value: string }[] {
  const rif = movimentoRiferimenti(m);
  return [
    { label: "Data registrazione", value: formatDateIt(rif.data_ora_registrazione) },
    { label: "Tipo operazione", value: movimentoCausaleCs(m) },
    { label: "Causale operazione", value: movimentoCausaleCompleta(m) },
    { label: "N. registrazione", value: movimentoNumeroRegistrazione(m) },
    { label: "Identificativo", value: movimentoIdentificativo(m) },
    { label: "Registro", value: String(rif.identificativo_registro ?? "—") },
    { label: "Data trasmissione", value: formatDateIt(rif.data_trasmissione, true) },
    { label: "Annullato", value: m.annullato ? "Sì" : "No" },
    { label: "Rettificato", value: m.rettificato ? "Sì" : "No" },
  ];
}

export function movimentoRifiutoCampi(m: MovimentoRecord): { label: string; value: string }[] {
  const r = movimentoRifiuto(m);
  const qty = asRecord(r.quantita);
  const hp = r.caratteristiche_pericolo ?? r.hp;
  const hpList = Array.isArray(hp) ? hp.join(", ") : hp ? String(hp) : "";
  const prov = r.provenienza ? (PROVENIENZA[String(r.provenienza)] ?? String(r.provenienza)) : "";
  const sf = r.stato_fisico ? (STATI_FISICI[String(r.stato_fisico)] ?? String(r.stato_fisico)) : "";
  const qVal = qty.valore != null ? `${qty.valore} ${qty.unita_misura ?? ""}`.trim() : "—";
  return [
    { label: "Codice EER", value: formatEerDisplay(r.codice_eer) },
    { label: "Descrizione del rifiuto", value: String(r.descrizione ?? "—") },
    { label: "Stato fisico", value: sf || "—" },
    { label: "Provenienza", value: prov || "—" },
    { label: "Caratt. di pericolo (HP)", value: hpList || "—" },
    { label: "Quantità", value: qVal },
    { label: "Verificato in partenza", value: r.verificato_in_partenza === true ? "Sì" : r.verificato_in_partenza === false ? "No" : "—" },
    { label: "Trasporto ADR", value: r.trasporto_adr === true ? "Sì" : r.trasporto_adr === false ? "No" : "—" },
  ];
}

function soggettoCampi(prefix: string, block: Record<string, unknown>): { label: string; value: string }[] {
  if (!Object.keys(block).length) return [];
  const ind = asRecord(block.indirizzo);
  const citta = asRecord(ind.citta);
  const addr = [ind.indirizzo, ind.civico, citta.nome_comune].filter(Boolean).join(", ");
  return [
    { label: `${prefix} — Denominazione`, value: String(block.denominazione ?? "—") },
    { label: `${prefix} — Codice fiscale`, value: String(block.codice_fiscale ?? "—") },
    { label: `${prefix} — Indirizzo`, value: addr || "—" },
  ];
}

export function movimentoProvenienzaCampi(m: MovimentoRecord): { label: string; value: string }[] {
  const r = movimentoRifiuto(m);
  const out: { label: string; value: string }[] = [];
  if (r.provenienza) {
    out.push({
      label: "Provenienza rifiuto",
      value: PROVENIENZA[String(r.provenienza)] ?? String(r.provenienza),
    });
  }
  out.push(...soggettoCampi("Produttore", asRecord(m.produttore)));
  out.push(...soggettoCampi("Trasportatore", asRecord(m.trasportatore)));
  out.push(...soggettoCampi("Destinatario", asRecord(m.destinatario)));
  const inter = m.intermediari ?? m.intermediario;
  const list = Array.isArray(inter) ? inter : inter ? [inter] : [];
  list.forEach((it, i) => {
    out.push(...soggettoCampi(`Intermediario ${i + 1}`, asRecord(it)));
  });
  return out.length ? out : [{ label: "—", value: "Nessun dato di provenienza/soggetti" }];
}

export function movimentoIntegrazioneCampi(m: MovimentoRecord): { label: string; value: string }[] {
  const fir = asRecord(m.integrazione_fir);
  if (!Object.keys(fir).length) return [];
  return Object.entries(fir).map(([k, v]) => ({
    label: k.replace(/_/g, " "),
    value: typeof v === "object" ? JSON.stringify(v, null, 2) : String(v ?? "—"),
  }));
}

export function movimentoEsitoCampi(m: MovimentoRecord): { label: string; value: string }[] {
  const esito = asRecord(m.esito);
  if (!Object.keys(esito).length) return [];
  return Object.entries(esito).map(([k, v]) => ({
    label: k.replace(/_/g, " "),
    value: typeof v === "object" ? JSON.stringify(v, null, 2) : String(v ?? "—"),
  }));
}
